import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Button, Divider
} from '@mui/material';
import { Home, Add, Edit, Delete, AccountBalance, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import Toast from './Toast';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { useGLAccounts } from '../hooks/useGLAccounts';
import { glAccountOptions } from '../utils/glAccountOptions';

const API = process.env.REACT_APP_API_URL;

const InfoRow = ({ label, value }) => (
  <Box display="flex" gap={1} mb={0.5}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 220, fontWeight: 500 }}>{label}</Typography>
    <Typography variant="body2">{value || <em style={{ color: '#bbb' }}>—</em>}</Typography>
  </Box>
);

const emptyForm = {
  groupCode: '',
  description: '',
  acquisitionCostAccount: '',
  accumulatedDepreciationAccount: '',
  depreciationExpenseAccount: '',
  gainOnDisposalAccount: '',
  lossOnDisposalAccount: '',
  disposalAccount: ''
};

const FAPostingGroupList = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: dialogOpen, value: form, onClose: () => setDialogOpen(false) });
  const [saving, setSaving]       = useState(false);
  const [viewGroup, setViewGroup] = useState(null);
  const [confirmDlg, setConfirmDlg] = useState(null);

  const { accounts: glAccounts } = useGLAccounts();
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchGroups = async () => {
    try {
      const r = await axios.get(`${API}/api/fixed-assets/posting-groups`, { headers });
      setGroups(r.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load posting groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGroups(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 2000);
    return () => clearTimeout(t);
  }, [success]);

  const handleCreate = () => {
    setEditingCode(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const handleEdit = (g) => {
    setEditingCode(g.groupCode);
    setForm({ ...emptyForm, ...g });
    setDialogOpen(true);
  };

  const handleDelete = (groupCode) => {
    setConfirmDlg({ groupCode });
  };

  const confirmAction = async () => {
    try {
      await axios.delete(`${API}/api/fixed-assets/posting-groups/${confirmDlg.groupCode}`, { headers });
      setSuccess('Posting group deleted');
      setConfirmDlg(null);
      fetchGroups();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
      setConfirmDlg(null);
    }
  };

  const handleSave = async () => {
    if (!form.groupCode.trim()) return setError('Group Code is required');
    if (!form.acquisitionCostAccount)       return setError('Acquisition Cost Account is required');
    if (!form.accumulatedDepreciationAccount) return setError('Accumulated Depreciation Account is required');
    if (!form.depreciationExpenseAccount)   return setError('Depreciation Expense Account is required');

    setSaving(true);
    setError('');
    try {
      if (editingCode) {
        await axios.put(`${API}/api/fixed-assets/posting-groups/${editingCode}`, form, { headers });
        setSuccess('Posting group updated');
      } else {
        await axios.post(`${API}/api/fixed-assets/posting-groups`, form, { headers });
        setSuccess('Posting group created');
      }
      setDialogOpen(false);
      fetchGroups();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const AccountSelect = ({ label, fieldKey, required = false }) => (
    <TextField
      select label={label} value={form[fieldKey] || ''} onChange={set(fieldKey)}
      fullWidth size="small" sx={{ mb: 2 }}
      // Keep the label floated so it never overlaps the "— Not set —" placeholder
      InputLabelProps={{ shrink: true }}
      SelectProps={{ displayEmpty: true }}
    >
      {glAccountOptions(glAccounts, { includeNone: !required })}
    </TextField>
  );

  if (loading) return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading...</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box>
              <Typography variant="h5" color="primary">FA Posting Groups</Typography>
              <Typography variant="body2" color="text.secondary">
                Map Fixed Asset classes to GL accounts for acquisition, depreciation and disposal
              </Typography>
            </Box>
            <Box>
              <IconButton onClick={() => navigate('/fixed-assets')} title="Fixed Assets"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' }, mr: 1 }}>
                <Home />
              </IconButton>
              <IconButton onClick={handleCreate} title="New Posting Group"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Add />
              </IconButton>
            </Box>
          </Box>

          <Toast error={error} success={success} onClose={(k) => k === 'error' ? setError('') : setSuccess('')} />

          {groups.length === 0 ? (
            <Typography color="text.secondary">
              No posting groups yet. Create one to link Fixed Assets to GL accounts.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Code</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Acquisition Cost Acc.</strong></TableCell>
                  <TableCell><strong>Accum. Depr. Acc.</strong></TableCell>
                  <TableCell><strong>Depr. Expense Acc.</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map(g => (
                  <TableRow key={g.groupCode} hover sx={{ cursor: 'pointer' }}
                    onClick={() => setViewGroup(g)}>
                    <TableCell><strong>{g.groupCode}</strong></TableCell>
                    <TableCell>{g.description}</TableCell>
                    <TableCell>
                      <Typography variant="caption">{g.acquisitionCostAccount}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{g.accumulatedDepreciationAccount}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{g.depreciationExpenseAccount}</Typography>
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <IconButton size="small" onClick={() => handleEdit(g)}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => handleDelete(g.groupCode)} color="error"><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── View card ── */}
      <Dialog open={Boolean(viewGroup)} onClose={() => setViewGroup(null)} maxWidth="sm" fullWidth>
        {viewGroup && (
          <>
            <DialogTitle component="div">
              <Box display="flex" alignItems="center" gap={1}>
                <AccountBalance color="primary" />
                <Box>
                  <Typography variant="h6" lineHeight={1}>{viewGroup.groupCode}</Typography>
                  <Typography variant="caption" color="text.secondary">{viewGroup.description || 'FA Posting Group'}</Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="subtitle2" color="primary" gutterBottom>Required Accounts</Typography>
              <InfoRow label="Acquisition Cost Account"       value={viewGroup.acquisitionCostAccount} />
              <InfoRow label="Accumulated Depreciation Acc."  value={viewGroup.accumulatedDepreciationAccount} />
              <InfoRow label="Depreciation Expense Account"   value={viewGroup.depreciationExpenseAccount} />

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>Disposal Accounts</Typography>
              <InfoRow label="Gain on Disposal Account" value={viewGroup.gainOnDisposalAccount} />
              <InfoRow label="Loss on Disposal Account" value={viewGroup.lossOnDisposalAccount} />
              <InfoRow label="Disposal Account"         value={viewGroup.disposalAccount} />
            </DialogContent>
            <DialogActions>
              <Button startIcon={<Edit />}
                onClick={() => { setViewGroup(null); handleEdit(viewGroup); }}>
                Edit
              </Button>
              <Button variant="outlined" onClick={() => setViewGroup(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Confirm Delete dialog ── */}
      <Dialog open={Boolean(confirmDlg)} onClose={() => setConfirmDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>Delete FA Posting Group <strong>{confirmDlg?.groupCode}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmAction}>Delete</Button>
        </DialogActions>
      </Dialog>

      {discardDialog}
      <Dialog open={dialogOpen} onClose={attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCode ? `Edit — ${editingCode}` : 'New FA Posting Group'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField label="Group Code *" value={form.groupCode} onChange={set('groupCode')}
            fullWidth size="small" sx={{ mb: 2 }}
            disabled={!!editingCode}
            helperText={editingCode ? 'Code cannot be changed after creation' : ''} />

          <TextField label="Description" value={form.description} onChange={set('description')}
            fullWidth size="small" sx={{ mb: 2 }} />

          <Divider sx={{ mb: 2 }}><Typography variant="caption" color="text.secondary">Required Accounts</Typography></Divider>

          <AccountSelect label="Acquisition Cost Account *"       fieldKey="acquisitionCostAccount"       required />
          <AccountSelect label="Accumulated Depreciation Account *" fieldKey="accumulatedDepreciationAccount" required />
          <AccountSelect label="Depreciation Expense Account *"   fieldKey="depreciationExpenseAccount"   required />

          <Divider sx={{ mb: 2 }}><Typography variant="caption" color="text.secondary">Disposal Accounts (optional)</Typography></Divider>

          <AccountSelect label="Gain on Disposal Account" fieldKey="gainOnDisposalAccount" />
          <AccountSelect label="Loss on Disposal Account" fieldKey="lossOnDisposalAccount" />
          <AccountSelect label="Disposal Account"         fieldKey="disposalAccount" />
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FAPostingGroupList;
